import React, { FC, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { Tag, Box, TextButton } from '@wix/design-system';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { GetPlaceResponse } from '@wix/ambassador-atlas-v2-place/types';
import { Pricing } from '@wix/ambassador-partners-profile-v1-partner-profile/types';
import { useExploreContext } from '@components/Contexts/ExploreContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { updateQueryParams } from '@utils/route';
import { generateLocationLabel } from '@utils/locationLabel';
import { MIN_PRICE, DEFAULT_PRICING } from '@utils/constants';
import { useBi } from '@components/Hooks/useBi';
import useFormattedCurrency from '@components/Hooks/useFormattedCurrency';
import { Language } from '@common/types';
import { useShallowRouter } from '@components/Hooks/useRouter';
import s from './SelectedFilterTags.module.scss';

const MAX_TAGS = 5;

const SelectedFilterTags: FC = () => {
  const { t } = useTranslation();
  const logger = useBi();
  const { navigate, router } = useShallowRouter();
  const { sessionId } = useBiSessionContext();

  const {
    state: {
      selectedServices,
      selectedLanguages,
      selectedPricing,
      selectedLocation,
      currentPage,
      entry,
      origin,
      maxPrice,
      userCurrency,
    },
  } = useExploreContext();

  const [showAll, setShowAll] = useState(false);
  const [filteredServices, setFilteredServices] = useState<any>([]);
  const [filteredLanguages, setFilteredLanguages] = useState<any>([]);
  const [filteredLocation, setFilteredLocation] = useState({});
  const [filteredBudget, setFilteredBudget] = useState({});
  const { currencyFormatter } = useFormattedCurrency({
    currency: userCurrency,
  });

  const [tags, setTags] = useState<any>([]);
  const hasPriceFilter = useMemo(() => {
    if (selectedPricing.min !== MIN_PRICE) {
      return true;
    } else if (
      _.isNumber(selectedPricing.max) &&
      !_.isNaN(selectedPricing.max)
    ) {
      return true;
    }
    return false;
  }, [selectedPricing.max, selectedPricing.min]);

  const hasLocationFilter = useMemo(() => {
    return Boolean(selectedLocation);
  }, [selectedLocation]);

  useEffect(() => {
    const _services = _.map(selectedServices, (service) => {
      return {
        key: service.id,
        dataHook: `tag-service-${service.id}`,
        content: `explore.services.${service.displayKey}`,
        onRemove: () => {
          const _selectedOptions = _.filter(
            selectedServices,
            (selectedService) => {
              return service.id !== selectedService.id;
            },
          );
          const servicesDisplayKeys = _selectedOptions.map(
            (selectedService) => selectedService.displayKey,
          );
          const serviceIds = servicesDisplayKeys.join(',');
          navigate(
            `/${updateQueryParams(
              {
                serviceIds,
              },
              router,
            )}`,
            false,
          );

          biEventsLogger({
            clickType: 'clear services filter from tags',
            biSelectedServices: servicesDisplayKeys as Array<string>,
          });
        },
      };
    });
    setFilteredServices(_services);
  }, [selectedServices]);

  useEffect(() => {
    const _languages = _.map(selectedLanguages, (lang) => {
      return {
        key: lang.id,
        dataHook: `tag-language-${lang.id}`,
        content: lang.value,
        onRemove: () => {
          const _selectedOptions = _.filter(selectedLanguages, (s) => {
            return s.id !== lang.id;
          });

          navigate(
            `/${updateQueryParams(
              {
                lang: _selectedOptions.map((o) => o.id).join(','),
              },
              router,
            )}`,
          );

          biEventsLogger({
            clickType: 'clear language filter from tags',
            biSelectedLanguages: _selectedOptions,
          });
        },
      };
    });

    setFilteredLanguages(_languages);
  }, [selectedLanguages]);

  useEffect(() => {
    const _price = {
      key: 'min',
      dataHook: 'tag-pricing-',
      onRemove: () => {
        navigate(
          `/${updateQueryParams(
            {
              min: MIN_PRICE,
              max: undefined,
            },
            router,
          )}`,
        );
        biEventsLogger({
          clickType: 'clear budget filter from tags',
          biSelectedPricing: DEFAULT_PRICING,
        });
      },
      content: `${currencyFormatter(selectedPricing.min ?? 0)} -
        ${currencyFormatter(
          selectedPricing.max ? selectedPricing.max : maxPrice,
        )}${!selectedPricing.max ? '+' : ''}`,
    };
    setFilteredBudget(_price);
  }, [selectedPricing.max, selectedPricing.min, userCurrency]);

  useEffect(() => {
    const _location = {
      key: 'location',
      dataHook: 'tag-location',
      onRemove: () => {
        navigate(
          `/${updateQueryParams(
            {
              location: undefined,
            },
            router,
          )}`,
        );
        biEventsLogger({
          clickType: 'clear location filter from tags',
          biSelectedLocation: undefined,
        });
      },
      content: selectedLocation?.address
        ? generateLocationLabel(selectedLocation.address)
        : '',
    };
    setFilteredLocation(_location);
  }, [selectedLocation?.address]);

  useEffect(() => {
    // order of tags: service, location, language, budget
    let _tags = [...filteredServices];
    if (hasLocationFilter) {
      _tags.push(filteredLocation);
    }
    _tags = [..._tags, ...filteredLanguages];
    if (hasPriceFilter) {
      _tags.push(filteredBudget);
    }
    setTags(_tags);
  }, [
    filteredBudget,
    filteredLanguages,
    filteredLocation,
    filteredServices,
    hasLocationFilter,
    hasPriceFilter,
  ]);

  function renderTag({
    key,
    dataHook,
    onRemove,
    content,
  }: {
    key: string;
    dataHook: string;
    onRemove: (id: string) => void;
    content: string;
  }) {
    return (
      <Tag
        size="medium"
        key={key}
        dataHook={dataHook}
        id="standard"
        theme="neutral"
        onRemove={onRemove}
      >
        {content}
      </Tag>
    );
  }

  const biEventsLogger = ({
    clickType,
    biSelectedServices,
    biSelectedLanguages,
    biSelectedPricing,
    biSelectedLocation,
  }: {
    clickType: string;
    biSelectedServices?: Array<string>;
    biSelectedLanguages?: Array<Language>;
    biSelectedPricing?: Pricing;
    biSelectedLocation?: GetPlaceResponse['place'];
  }) => {
    logger.report(
      exploreResultsPageGenericClick({
        clickType,
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: JSON.stringify({
          currentPage,
          selectedFilters: [
            { selectedServices: biSelectedServices || selectedServices },
            { selectedLanguages: biSelectedLanguages || selectedLanguages },
            { selectedPricing: biSelectedPricing || selectedPricing },
            { selectedLocation: biSelectedLocation || selectedLocation },
          ],
        }),
      }),
    );
  };

  const ClearAllFiltersButton = () => {
    return (
      <TextButton
        dataHook="tag-clear-all"
        underline="always"
        skin="dark"
        size="small"
        onClick={() => {
          logger.report(
            exploreResultsPageGenericClick({
              clickType: 'remove all filters',
              sessionId,
              origin,
              entry,
              exploreVersion: 'V2',
              data: JSON.stringify({ currentPage }),
            }),
          );
          navigate(
            updateQueryParams({}, router, {
              services: false,
              language: false,
              location: false,
              all: true,
            }),
            false,
          );
          setShowAll(false);
        }}
      >
        {t('fliters-clear-all')}
      </TextButton>
    );
  };

  if (_.size(tags) === 0) {
    return <></>;
  }

  return (
    <Box
      marginTop="18px"
      marginBottom="18px"
      marginRight="50px"
      dataHook="selection-tags"
    >
      <div
        data-hook="selection-tags-container"
        className={s.root}
        aria-label="filter by"
        role="group"
      >
        {_.map(tags.slice(0, MAX_TAGS), (tag: any) => {
          return renderTag({
            ...tag,
            content: t(tag.content),
          });
        })}
        {_.size(tags) > MAX_TAGS &&
          (!showAll ? (
            <>
              <Tag
                id="standard"
                size="medium"
                dataHook="tag-more-count"
                theme="lightOutlined"
                removable={false}
                onClick={() => {
                  setShowAll(true);
                }}
              >
                {t('fliters-more-count', {
                  moreCount: _.size(tags) - MAX_TAGS,
                })}
              </Tag>
            </>
          ) : (
            <>
              {_.map(tags.slice(MAX_TAGS, _.size(tags)), (tag: any) => {
                return renderTag({
                  ...tag,
                  content: t(tag.content),
                });
              })}
              <Tag
                id="standard"
                size="medium"
                dataHook="tag-show-less"
                theme="lightOutlined"
                removable={false}
                onClick={() => {
                  setShowAll(false);
                }}
              >
                {t('fliters-show-less')}
              </Tag>
            </>
          ))}
        <ClearAllFiltersButton />
      </div>
    </Box>
  );
};

export default SelectedFilterTags;
